import { formatTime, type ZmanId } from "@kedaat/engine";
import { groupRows, type DayPartId } from "@/lib/groups";
import { skyColorAt, skyMarks } from "@/lib/sky";
import { formatCountdown, type ZmanRow } from "@/lib/zmanim";
import { MoonIcon, SunIcon, SunriseIcon } from "./icons";

const PART_ICON: Record<DayPartId, typeof SunIcon> = {
  morning: SunriseIcon,
  afternoon: SunIcon,
  evening: MoonIcon,
};

export function ZmanimList({
  rows,
  now,
  seconds,
  onOpen,
}: {
  rows: ZmanRow[];
  now: number | null;
  seconds: boolean;
  onOpen: (zman: ZmanId) => void;
}) {
  const nextId =
    now === null ? null : (rows.find((r) => r.ms !== null && r.ms > now)?.zman.id ?? null);
  const marks = skyMarks(rows);

  return (
    <section aria-label="זמני היום" className="space-y-4">
      {groupRows(rows).map((part) => (
        <div key={part.id}>
          <h3 className="mb-1.5 flex items-center gap-1.5 px-1 text-xs font-semibold tracking-wide text-muted">
            {(() => {
              const Icon = PART_ICON[part.id];
              return <Icon className="size-4" />;
            })()}
            {part.label}
          </h3>
          <ol className="divide-y divide-line overflow-hidden rounded-2xl border border-line bg-card shadow-sm">
            {part.rows.map((row) => {
              const isNext = row.zman.id === nextId;
              const isPast = now !== null && row.ms !== null && row.ms <= now;
              return (
                <li
                  key={row.zman.id}
                  aria-current={isNext ? "time" : undefined}
                  className="relative"
                >
                  {/* A stripe in the color of the sky at that hour, so the list reads as one day. */}
                  {marks && row.ms !== null && (
                    <span
                      aria-hidden="true"
                      className="absolute inset-y-0 start-0 w-1.5"
                      style={{ background: skyColorAt(row.ms, marks) }}
                    />
                  )}
                  <button
                    type="button"
                    onClick={() => onOpen(row.zman.id)}
                    aria-haspopup="dialog"
                    className={`group flex w-full items-center justify-between gap-4 py-3 pe-4 ps-5 text-start transition-colors hover:bg-accent-soft/60 ${
                      isNext ? "bg-accent-soft" : ""
                    } ${isPast ? "text-muted" : ""}`}
                  >
                    <span className="min-w-0">
                      <span className="flex items-center gap-2 font-medium">
                        {isNext && (
                          <span className="relative flex size-2 shrink-0" aria-hidden="true">
                            <span className="absolute inline-flex size-full animate-ping rounded-full bg-accent opacity-60" />
                            <span className="relative inline-flex size-2 rounded-full bg-accent" />
                          </span>
                        )}
                        {row.zman.label.he}
                      </span>
                      <span className="block truncate text-xs text-muted">
                        {row.opinion.label.he}
                        {row.overridden && (
                          <span className="ms-1.5 rounded-full bg-accent/15 px-1.5 text-accent">
                            מותאם
                          </span>
                        )}
                        {isNext && now !== null && row.ms !== null && (
                          <span className="font-medium text-accent">
                            {" "}
                            · {formatCountdown(row.ms - now)}
                          </span>
                        )}
                      </span>
                    </span>
                    <span className="flex shrink-0 items-center gap-2">
                      <span
                        className={`text-2xl tabular-nums ${isNext ? "font-bold text-accent" : "font-semibold"}`}
                      >
                        {row.ms === null
                          ? "—"
                          : formatTime(row.ms, row.opinion.rounding, { seconds })}
                      </span>
                      <span
                        className="text-lg leading-none text-muted transition-transform group-hover:-translate-x-0.5"
                        aria-hidden="true"
                      >
                        ‹
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
        </div>
      ))}
    </section>
  );
}
